import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, CheckCircle, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { passwordResetAPI, PasswordResetInfo } from '../lib/api';
import { cabinetUrl } from '../lib/domains';

export const PasswordResetPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const [resetInfo, setResetInfo] = useState<PasswordResetInfo | null>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        const loadResetInfo = async () => {
            if (!token) {
                setError('Ссылка сброса пароля недействительна');
                setIsLoading(false);
                return;
            }

            try {
                const info = await passwordResetAPI.getInfo(token);
                setResetInfo(info);
            } catch (err: any) {
                setError(err.response?.data?.detail || 'Ссылка сброса пароля недействительна');
            } finally {
                setIsLoading(false);
            }
        };

        loadResetInfo();
    }, [token]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!token) {
            setError('Ссылка сброса пароля недействительна');
            return;
        }

        const minimumPasswordLength = resetInfo?.user_type === 'merchant' ? 10 : 6;
        if (password.length < minimumPasswordLength) {
            setError(`Пароль должен содержать минимум ${minimumPasswordLength} символов`);
            return;
        }

        if (password !== confirmPassword) {
            setError('Пароли не совпадают');
            return;
        }

        setIsSubmitting(true);
        try {
            const result = await passwordResetAPI.complete(token, password);
            setSuccess(result.message || 'Пароль успешно обновлен');
            setTimeout(() => window.location.assign(result.redirect_url || '/'), 1200);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'Не удалось обновить пароль');
        } finally {
            setIsSubmitting(false);
        }
    };

    const loginUrl = resetInfo?.user_type === 'support'
        ? cabinetUrl('support', '/login')
        : resetInfo?.user_type === 'merchant'
            ? cabinetUrl('merchant', '/login')
            : cabinetUrl('trader', '/login');
    const minimumPasswordLength = resetInfo?.user_type === 'merchant' ? 10 : 6;

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center py-12 px-4">
            <div className="w-full max-w-md space-y-6">
                <div className="text-center">
                    <a href={loginUrl} className="inline-flex items-center text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 mb-4">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Вернуться ко входу
                    </a>
                    <div className="mx-auto w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
                        <KeyRound className="h-8 w-8 text-slate-700 dark:text-slate-200" />
                    </div>
                </div>

                <Card className="shadow-lg">
                    <CardHeader>
                        <CardTitle>Сброс пароля</CardTitle>
                        <CardDescription>
                            {resetInfo
                                ? `Аккаунт: ${resetInfo.username}`
                                : 'Проверяем ссылку сброса пароля'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {isLoading ? (
                            <p className="text-sm text-slate-500 dark:text-slate-400">Загрузка...</p>
                        ) : error && !resetInfo ? (
                            <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2 text-red-600 dark:text-red-300 text-sm">
                                <AlertCircle className="h-4 w-4" />
                                <span>{error}</span>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {error && (
                                    <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md flex items-center gap-2 text-red-600 dark:text-red-300 text-sm">
                                        <AlertCircle className="h-4 w-4" />
                                        <span>{error}</span>
                                    </div>
                                )}

                                {success && (
                                    <div className="p-3 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md flex items-center gap-2 text-green-700 dark:text-green-300 text-sm">
                                        <CheckCircle className="h-4 w-4" />
                                        <span>{success}</span>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label htmlFor="password">Новый пароль</Label>
                                    <Input
                                        id="password"
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        minLength={minimumPasswordLength}
                                        required
                                        disabled={isSubmitting || Boolean(success)}
                                    />
                                </div>
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    Не менее {minimumPasswordLength} символов
                                </p>

                                <div className="space-y-2">
                                    <Label htmlFor="confirm-password">Подтвердите пароль</Label>
                                    <Input
                                        id="confirm-password"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        minLength={minimumPasswordLength}
                                        required
                                        disabled={isSubmitting || Boolean(success)}
                                    />
                                </div>

                                <Button
                                    type="submit"
                                    className="w-full"
                                    disabled={isSubmitting || Boolean(success)}
                                >
                                    {isSubmitting ? 'Обновление...' : 'Обновить пароль'}
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};
